import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Statistic,
  DatePicker,
  Space,
  Typography,
  Table,
  Tag,
  Empty,
  Skeleton,
  Tooltip as AntTooltip,
} from "antd";
import {
  ApiOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  KeyOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import { useMutations } from "../../../hooks/useApiMutation";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const PRESETS = [
  { label: "Last 7 days", value: [dayjs().subtract(6, "day"), dayjs()] },
  { label: "Last 30 days", value: [dayjs().subtract(29, "day"), dayjs()] },
  { label: "This month", value: [dayjs().startOf("month"), dayjs()] },
  { label: "Last 90 days", value: [dayjs().subtract(89, "day"), dayjs()] },
];

const formatNumber = (n) => new Intl.NumberFormat().format(n || 0);

const AnalyticsTab = ({ agent }) => {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const [range, setRange] = useState([
    dayjs().subtract(29, "day"),
    dayjs(),
  ]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [from, to] = range;
      const { data: resp } = await mutateRef.current("fetchAgentAnalytics", {
        method: "get",
        endpoint: `/v1/agents/${agent.agentId}/analytics`,
        params: {
          from: from?.startOf("day").toISOString(),
          to: to?.endOf("day").toISOString(),
        },
      });
      setData(resp?.data || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [agent.agentId, range]);

  useEffect(() => {
    load();
  }, [load]);

  const chartData = useMemo(
    () =>
      (data?.dailySeries || []).map((p) => ({
        date: dayjs(p.date).format("MMM DD"),
        calls: p.calls,
        billed: p.tokensBilled,
        calculated: p.tokensCalculated,
      })),
    [data]
  );

  const keyColumns = [
    {
      title: "Key",
      dataIndex: "keyPrefix",
      key: "keyPrefix",
      render: (p, r) => (
        <Space direction="vertical" size={0}>
          <Text code style={{ fontSize: 12 }}>{p}…</Text>
          {r.label && <Text type="secondary" style={{ fontSize: 11 }}>{r.label}</Text>}
        </Space>
      ),
    },
    {
      title: "Env",
      dataIndex: "environment",
      key: "environment",
      render: (e) => <Tag color={e === "live" ? "green" : "gold"}>{e}</Tag>,
    },
    {
      title: "Calls",
      dataIndex: "calls",
      key: "calls",
      align: "right",
      render: (v) => formatNumber(v),
    },
    {
      title: "Tokens billed",
      dataIndex: "tokensBilled",
      key: "tokensBilled",
      align: "right",
      render: (v) => formatNumber(v),
    },
  ];

  const endpointColumns = [
    { title: "Endpoint", dataIndex: "endpoint", key: "endpoint" },
    {
      title: "Calls",
      dataIndex: "calls",
      key: "calls",
      align: "right",
      render: (v) => formatNumber(v),
    },
    {
      title: "Tokens billed",
      dataIndex: "tokensBilled",
      key: "tokensBilled",
      align: "right",
      render: (v) => formatNumber(v),
    },
  ];

  if (loading && !data) {
    return <Skeleton active paragraph={{ rows: 10 }} />;
  }

  return (
    <div>
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <Text strong style={{ fontSize: 16 }}>
            Usage analytics
          </Text>
          <div>
            <Text type="secondary">
              Aggregated from the TokenUsages ledger. Test traffic is shown
              separately and excluded from billing.
            </Text>
          </div>
        </div>
        <RangePicker
          value={range}
          onChange={(v) => v && v[0] && v[1] && setRange(v)}
          presets={PRESETS}
          allowClear={false}
          disabledDate={(d) => d && d.isAfter(dayjs().endOf("day"))}
        />
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title={
                <Space size={4}>
                  Total calls
                  <AntTooltip title="All API calls during the selected window, including test traffic.">
                    <InfoCircleOutlined style={{ color: "var(--text-muted)" }} />
                  </AntTooltip>
                </Space>
              }
              value={formatNumber(data?.totalCalls)}
              prefix={<ApiOutlined style={{ color: "var(--accent-blue)" }} />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatNumber(data?.testCalls)} in test mode
            </Text>
          </Card>
        </Col>

        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Tokens calculated"
              value={formatNumber(data?.totalTokensCalculated)}
              prefix={<ThunderboltOutlined style={{ color: "var(--accent-amber)" }} />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Raw tokens consumed by the provider
            </Text>
          </Card>
        </Col>

        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Tokens billed"
              value={formatNumber(data?.billableTokensBilled)}
              prefix={<DollarOutlined style={{ color: "var(--accent-teal)" }} />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              After billing multiplier, test excluded
            </Text>
          </Card>
        </Col>

        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Active keys"
              value={formatNumber(data?.uniqueKeys)}
              prefix={<KeyOutlined style={{ color: "var(--accent-violet)" }} />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              kb_ak_ keys with traffic
            </Text>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={16}>
          <Card size="small" title="Usage over time">
            {chartData.length === 0 ? (
              <Empty description="No data in this range" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 11 }}
                  />
                  <ReTooltip
                    formatter={(v) => formatNumber(v)}
                    labelStyle={{ color: "var(--text-primary)" }}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="calls"
                    name="Calls"
                    stroke="var(--accent-blue)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="billed"
                    name="Tokens billed"
                    stroke="var(--accent-teal)"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card size="small" title="Tokens: calculated vs billed">
            {chartData.length === 0 ? (
              <Empty description="No data" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <ReTooltip formatter={(v) => formatNumber(v)} />
                  <Legend />
                  <Bar
                    dataKey="calculated"
                    name="Calculated"
                    fill="var(--accent-amber)"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="billed"
                    name="Billed"
                    fill="var(--accent-teal)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card size="small" title="Top keys by usage">
            <Table
              rowKey="agentApiKeyId"
              columns={keyColumns}
              dataSource={data?.topKeys || []}
              pagination={false}
              size="small"
              locale={{ emptyText: "No key activity yet" }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card size="small" title="Top endpoints">
            <Table
              rowKey="endpoint"
              columns={endpointColumns}
              dataSource={data?.topEndpoints || []}
              pagination={false}
              size="small"
              locale={{ emptyText: "No traffic yet" }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AnalyticsTab;
